import React from 'react';
import { WizardData } from '../ProbateWizard';
import { EstateAssets, EstateProfile } from '../../types';
import { InputsUsedPanel } from '../results/InputsUsedPanel';
import { EstateBuilder } from '../EstateBuilder';
import { Fieldset, TextInput } from '../ui';
import { deriveReviewFlags, ReviewFlag, ReviewFlagSeverity } from '../probate/deriveReviewFlags';

interface Props {
    data: WizardData;
    updateAssets: (assets: EstateAssets) => void;
    updateProfile: (profile: EstateProfile) => void;
    goToStep?: (stepId: string) => boolean;
}

const SEVERITY_TONE: Record<ReviewFlagSeverity, { label: string; classes: string }> = {
    high:   { label: 'High',   classes: 'border-[#7A5A00] bg-[#FAF3E0] text-[#7A5A00]' },
    medium: { label: 'Medium', classes: 'border-accent bg-accent-soft text-accent' },
    low:    { label: 'Low',    classes: 'border-rule bg-paper text-ink3' },
};

const SEVERITY_ORDER: Record<ReviewFlagSeverity, number> = { high: 0, medium: 1, low: 2 };

export const SummaryStep: React.FC<Props> = ({ data, updateAssets, updateProfile, goToStep }) => {
    const profile = data.EstateProfile;
    const deceased = profile.deceased || {};
    const family = profile.family || {};

    // Wave 4 — review flags surfaced just before audit-run, no calc work yet.
    const reviewFlags = React.useMemo(
        () => deriveReviewFlags(profile, data.EstateAssets)
            .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]),
        [profile, data.EstateAssets]
    );

    return (
        <div className="space-y-12">
            <p className="font-serif italic text-[15px] text-ink2 leading-relaxed max-w-[560px]">
                A final read-through before we run the audit. Anything looks wrong, jump
                back to fix it — your inputs are saved.
            </p>

            {reviewFlags.length > 0 && (
                <ReviewFlagsPanel flags={reviewFlags} goToStep={goToStep} />
            )}

            {/* §9 — Optional report-only identity fields. Never used in calculations. */}
            <section className="border-b border-rule pb-8">
                <div className="text-[10px] tracking-eyebrow uppercase text-ink3 font-semibold mb-4">
                    For the report (optional)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    <Fieldset
                        label="Name of the deceased"
                        help="Optional — appears on the generated report. Doesn't affect calculations."
                    >
                        <TextInput
                            type="text"
                            placeholder="e.g. John Smith"
                            value={deceased.firstName || ''}
                            onChange={(e) =>
                                updateProfile({
                                    ...profile,
                                    deceased: { ...deceased, firstName: e.target.value },
                                })
                            }
                        />
                    </Fieldset>

                    <Fieldset
                        label="Executor or person completing this"
                        help="Optional — appears as the report addressee."
                    >
                        <TextInput
                            type="text"
                            placeholder="e.g. Ms E. Holloway"
                            value={family.executor_name || ''}
                            onChange={(e) =>
                                updateProfile({
                                    ...profile,
                                    family: { ...family, executor_name: e.target.value },
                                })
                            }
                        />
                    </Fieldset>
                </div>
            </section>

            <InputsUsedPanel profile={profile} goToStep={goToStep} />

            <section>
                <h3 className="font-serif text-[18px] text-ink mb-4">Estate assets</h3>
                <EstateBuilder data={data.EstateAssets} onChange={updateAssets} />
            </section>

            <div className="h-12" />
        </div>
    );
};

// ── "Things to double-check" panel ────────────────────────────────────────
interface ReviewFlagsPanelProps {
    flags: ReviewFlag[];
    goToStep?: (stepId: string) => boolean;
}

const ReviewFlagsPanel: React.FC<ReviewFlagsPanelProps> = ({ flags, goToStep }) => {
    return (
        <section className="border border-rule bg-white">
            <header className="border-b border-rule px-6 py-4">
                <h3 className="font-serif text-[18px] text-ink m-0">Things to double-check</h3>
                <p className="text-[12px] text-ink3 mt-1 leading-relaxed max-w-[560px]">
                    These points do not necessarily mean anything is wrong, but they may affect
                    the accuracy of the inheritance tax estimate or the forms needed.
                </p>
            </header>
            <ul className="divide-y divide-rule">
                {flags.map(flag => {
                    const tone = SEVERITY_TONE[flag.severity];
                    const canEdit = !!flag.stepId && !!goToStep;
                    return (
                        <li key={flag.id} className="px-6 py-4">
                            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                                <div className="flex items-baseline gap-3 min-w-0">
                                    <span className={`text-[10px] font-mono tracking-[0.06em] uppercase border px-2 py-0.5 ${tone.classes}`}>
                                        {tone.label}
                                    </span>
                                    <h4 className="font-serif text-[15px] text-ink m-0">{flag.title}</h4>
                                </div>
                                {canEdit && (
                                    <button
                                        type="button"
                                        onClick={() => goToStep!(flag.stepId!)}
                                        className="text-[11px] text-ink3 hover:text-accent underline decoration-rule underline-offset-4 transition-colors"
                                    >
                                        Edit answer
                                    </button>
                                )}
                            </div>
                            <p className="text-[13px] text-ink2 leading-relaxed mt-2">
                                {flag.message}
                            </p>
                            {flag.affectedLabels && flag.affectedLabels.length > 0 && (
                                <p className="text-[12px] text-ink3 mt-2">
                                    Affected: {flag.affectedLabels.join(', ')}
                                </p>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};
